/**
 * PHP2Shell (CVE-2026-109618, CVE-2026-109619): which PHP versions are fixed.
 *
 * Pure and dependency-free, so the checker page can run the same verdict in the
 * browser when a visitor types in a version they found by hand, and the API
 * route can run it on a version it read from a response header.
 *
 * Source: the php.net security releases of 21 October 2026. 8.2.35, 8.3.36,
 * 8.4.27 and 8.5.12 fix both CVEs. 8.1 and older get no upstream release; the
 * PHP security team published source backports for 7.4, 8.0 and 8.1 on GitHub
 * branches, and distributions/LTS vendors ship their own backported builds.
 * 8.6.0 RC4 carries the fix; earlier 8.6 pre-releases do not.
 */

/** First fixed patch release per supported branch. */
export const PHP2SHELL_FIXED: Record<string, number> = {
  "8.2": 35,
  "8.3": 36,
  "8.4": 27,
  "8.5": 12,
};

export const PHP2SHELL_FIXED_LABEL = "8.5.12, 8.4.27, 8.3.36 or 8.2.35";

export type ParsedPhpVersion = {
  /** As found, e.g. "8.1.2-1ubuntu2.20". */
  raw: string;
  major: number;
  minor: number;
  patch: number;
  /** "RC3", "alpha1", "-dev" and the like, lowercased. Empty for a GA release. */
  prerelease: string;
  /**
   * A distribution build tag such as "-1ubuntu2.20", "-1~deb12u1" or ".el9".
   * Distros backport security fixes without changing the upstream number, so a
   * build carrying one of these cannot be judged on the number alone.
   */
  distroSuffix: string;
};

export type Php2ShellVerdict =
  /** Upstream-supported branch, below the fixed release, no distro tag. */
  | "vulnerable"
  /** 8.1 or older, or a distro build below the fixed number: vulnerable unless a vendor backport is installed. */
  | "likely_vulnerable"
  | "patched";

export type VersionAssessment = {
  verdict: Php2ShellVerdict;
  /** "8.1" etc. */
  branch: string;
  /** The release to move to, when there is one on this branch. */
  fixedIn: string | null;
  reason: string;
};

const PRERELEASE_RE = /^(-?dev|alpha\d*|beta\d*|rc\d*)/i;

/**
 * Pull a PHP version out of any string that carries one: "PHP/8.3.12",
 * "8.1.2-1ubuntu2.20", "PHP Version 7.4.33", "Apache/2.4.58 (Ubuntu) PHP/8.3.6".
 * Returns null for anything that is not a plausible PHP version.
 */
export function parsePhpVersion(input: string): ParsedPhpVersion | null {
  const text = input.trim();
  // Prefer an explicit "PHP/x" or "PHP Version x" marker when there is one, so a
  // Server header's Apache version is never mistaken for PHP's.
  const marked = text.match(/php(?:\/|\s+version\s+|\s+)(\d+\.\d+(?:\.\d+)?[^\s,;()]*)/i);
  const bare = text.match(/^v?(\d+\.\d+(?:\.\d+)?[^\s,;()]*)$/i);
  const token = marked?.[1] ?? bare?.[1];
  if (!token) return null;

  const m = token.match(/^(\d+)\.(\d+)(?:\.(\d+))?(.*)$/);
  if (!m) return null;
  const major = Number(m[1]);
  const minor = Number(m[2]);
  const patch = m[3] === undefined ? NaN : Number(m[3]);
  // PHP 6 and 9 were never released, so a header claiming either is spoofed.
  // php.watch, for one, answers every path with "X-Powered-By: PHP/6.0.8".
  if (![4, 5, 7, 8].includes(major) || minor > 20) return null;

  let rest = m[4] ?? "";
  let prerelease = "";
  const pre = rest.match(PRERELEASE_RE);
  if (pre) {
    prerelease = pre[1].replace(/^-/, "").toLowerCase();
    rest = rest.slice(pre[0].length);
  }

  return {
    raw: token,
    major,
    minor,
    patch,
    prerelease,
    distroSuffix: rest.replace(/^[-+~.]+$/, ""),
  };
}

function rcNumber(prerelease: string): number | null {
  const m = prerelease.match(/^rc(\d+)$/);
  return m ? Number(m[1]) : null;
}

export function assessPhpVersion(v: ParsedPhpVersion): VersionAssessment {
  const branch = `${v.major}.${v.minor}`;

  // 8.6 is pre-release only. RC4 is the first build with the fix.
  if (v.major > 8 || (v.major === 8 && v.minor >= 6)) {
    const rc = rcNumber(v.prerelease);
    if (v.prerelease && (rc === null || rc < 4)) {
      return {
        verdict: "vulnerable",
        branch,
        fixedIn: `${branch}.0 RC4`,
        reason: `PHP ${branch} pre-releases before RC4 do not include the fix. Pre-release builds should not run in production.`,
      };
    }
    return {
      verdict: "patched",
      branch,
      fixedIn: null,
      reason: `PHP ${branch} builds from RC4 onward include the PHP2Shell fixes.`,
    };
  }

  const fixedPatch = PHP2SHELL_FIXED[branch];

  if (fixedPatch === undefined) {
    // 8.1 and older, all the way back to 5.x.
    return {
      verdict: "likely_vulnerable",
      branch,
      fixedIn: null,
      reason:
        `PHP ${branch} no longer receives releases from the PHP project, so there is no fixed ${branch} version. ` +
        "It is vulnerable unless your host or Linux distribution installed a backported fix. Upgrading to a supported branch is the safe route.",
    };
  }

  const fixedIn = `${branch}.${fixedPatch}`;

  if (Number.isNaN(v.patch)) {
    // "PHP/8.3" with no patch number: some hosts trim it. Cannot tell.
    return {
      verdict: "likely_vulnerable",
      branch,
      fixedIn,
      reason: `Only the branch (${branch}) is visible, not the patch number. Anything below ${fixedIn} is vulnerable.`,
    };
  }

  if (v.patch >= fixedPatch) {
    return {
      verdict: "patched",
      branch,
      fixedIn,
      reason: `PHP ${fixedIn} and later include the fixes for both PHP2Shell CVEs.`,
    };
  }

  if (v.distroSuffix) {
    return {
      verdict: "likely_vulnerable",
      branch,
      fixedIn,
      reason:
        `The upstream number is below ${fixedIn}, but this is a distribution build (${v.distroSuffix}). ` +
        "Distributions backport fixes without changing the number, so compare the full package version against your distribution's advisory.",
    };
  }

  return {
    verdict: "vulnerable",
    branch,
    fixedIn,
    reason: `PHP ${v.raw} is below ${fixedIn}, the first ${branch} release with the PHP2Shell fixes.`,
  };
}

const VERDICT_RANK: Record<Php2ShellVerdict, number> = {
  vulnerable: 2,
  likely_vulnerable: 1,
  patched: 0,
};

/** Worst verdict first, for when a site reports more than one PHP version. */
export function worseVerdict(a: Php2ShellVerdict, b: Php2ShellVerdict): Php2ShellVerdict {
  return VERDICT_RANK[a] >= VERDICT_RANK[b] ? a : b;
}
